/**
 * UTC date formatting and calendar arithmetic
 */

import type { TimeCadence } from "./report-configuration.ts";
import { Temporal } from "temporal-polyfill";

const DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  month: "short",
  day: "numeric",
  year: "numeric",
};

export type ISODate = `${number}-${number}-${number}`;

export const isISODate = (value: string): value is ISODate =>
  /^\d{4}-\d{2}-\d{2}$/.test(value) && parsePlainDate(value) !== undefined;

export const parsePlainDate = (
  value: string,
): Temporal.PlainDate | undefined => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return undefined;

  try {
    const date = Temporal.PlainDate.from(value, { overflow: "reject" });
    return date.year >= 1 ? date : undefined;
  } catch {
    return undefined;
  }
};

const withinISOYearRange = (
  date: Temporal.PlainDate,
): Temporal.PlainDate | undefined =>
  date.year >= 1 && date.year <= 9999 ? date : undefined;

export const toPlainDate = (value: unknown): Temporal.PlainDate | undefined => {
  if (value instanceof Temporal.PlainDate) return withinISOYearRange(value);

  if (typeof value === "string") {
    const plainDate = parsePlainDate(value);
    if (plainDate) return plainDate;

    const timestamp = /^(\d{4}-\d{2}-\d{2})T(.+)$/i.exec(value);
    if (!timestamp || !parsePlainDate(timestamp[1])) return undefined;

    let instantString = value;
    const offset = /([+-])(\d{2}):(\d{2})$/.exec(instantString);
    if (offset) {
      instantString = instantString.replace(
        /([+-])(\d{2}):(\d{2})$/,
        "$1$2:$3",
      );
    } else {
      const compactOffset = /([+-])(\d{2})(\d{2})$/.exec(instantString);
      if (compactOffset) {
        instantString = instantString.replace(
          /([+-])(\d{2})(\d{2})$/,
          "$1$2:$3",
        );
      } else if (!/[zZ]$/.test(instantString)) {
        instantString += "Z";
      }
    }

    try {
      return withinISOYearRange(
        Temporal.Instant.from(instantString)
          .toZonedDateTimeISO("UTC")
          .toPlainDate(),
      );
    } catch {
      return undefined;
    }
  }

  let epochMilliseconds: number;
  if (value instanceof Date) {
    epochMilliseconds = value.getTime();
  } else if (typeof value === "number") {
    epochMilliseconds = value;
  } else {
    return undefined;
  }

  if (!Number.isFinite(epochMilliseconds)) return undefined;
  try {
    return withinISOYearRange(
      Temporal.Instant.fromEpochMilliseconds(epochMilliseconds)
        .toZonedDateTimeISO("UTC")
        .toPlainDate(),
    );
  } catch {
    return undefined;
  }
};

export const toISODate = (value: unknown): ISODate => {
  const date = toPlainDate(value);
  if (!date) throw new RangeError(`Invalid ISO date: ${String(value)}`);
  return date.toString() as ISODate;
};

export const todayUTC = (): Temporal.PlainDate =>
  Temporal.Now.plainDateISO("UTC");

export const fromLocalCalendarDate = (date: Date): Temporal.PlainDate =>
  Temporal.PlainDate.from({
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
  });

export const toLocalCalendarDate = (value: string): Date => {
  const date = parsePlainDate(value);
  if (!date) throw new RangeError(`Invalid ISO date: ${value}`);
  return new Date(date.year, date.month - 1, date.day);
};

export const addCadence = (
  date: Temporal.PlainDate,
  cadence: TimeCadence | string,
  amount: number,
): Temporal.PlainDate => {
  if (!Number.isInteger(amount)) {
    throw new RangeError("Cadence amount must be an integer");
  }

  switch (cadence.toLowerCase()) {
    case "day":
      return date.add({ days: amount });
    case "week":
      return date.add({ weeks: amount });
    case "month":
      return date.add({ months: amount });
    case "quarter":
      return date.add({ months: amount * 3 });
    case "year":
      return date.add({ years: amount });
    default:
      throw new Error(`Unsupported time cadence: ${cadence}`);
  }
};

/**
 * Formats a Date, YYYY-MM-DD string, or ISO timestamp as "Jan 1, 2026" in UTC.
 * Timestamp offsets are normalized to UTC; timestamps without an offset use UTC.
 * @returns "Invalid Date" when the input cannot be parsed.
 */
export const formatDate = (date: Date | string | Temporal.PlainDate): string =>
  toPlainDate(date)?.toLocaleString("en-US", DATE_FORMAT_OPTIONS) ??
  "Invalid Date";

/**
 * Returns the UTC calendar date of a Date as YYYY-MM-DD, discarding its time.
 * Intended for dates with four-digit, non-negative years.
 * @throws {RangeError} If the Date is invalid.
 */
export const formatSQLDateString = (date: Date): string =>
  date.toISOString().slice(0, 10);

/**
 * Tests whether the end UTC date is strictly after the start's next anniversary.
 * Accepts Dates, epoch-millisecond numbers, YYYY-MM-DD strings, and ISO timestamps.
 * A February 29 anniversary clamps to February 28 in a non-leap year.
 * @returns false for invalid inputs, reversed ranges, or exactly one year.
 */
export const isDateRangeLongerThanOneYear = (
  startValue: unknown,
  endValue: unknown,
): boolean => {
  const start = toPlainDate(startValue);
  const end = toPlainDate(endValue);
  if (!start || !end) return false;

  return Temporal.PlainDate.compare(end, start.add({ years: 1 })) > 0;
};

/**
 * Parses a strict YYYY-MM-DD string into a new Date at UTC midnight.
 * @throws {RangeError} If the string is malformed or the date is impossible.
 */
export const parseUTCDateOnly = (value: string): Temporal.PlainDate => {
  const date = parsePlainDate(value);
  if (!date) throw new RangeError(`Invalid YYYY-MM-DD date: ${value}`);
  return date;
};

/**
 * Returns the signed number of UTC calendar days from start to end.
 * Both inputs must be YYYY-MM-DD strings. Equal dates return 0; consecutive
 * dates return 1. An earlier end date produces a negative result.
 * @throws {RangeError} If either date is invalid.
 */
export const daysBetween = (start: string, end: string): number =>
  parseUTCDateOnly(start).until(parseUTCDateOnly(end), { largestUnit: "day" })
    .days;

/**
 * Returns the start of a backward-looking snapshot interval as YYYY-MM-DD.
 * Accepts a YYYY-MM-DD string or ISO timestamp, normalized to its UTC date.
 * Day returns that date; Week, Month, Quarter, and Year subtract 7 days,
 * 1 month, 3 months, and 1 year respectively, clamping month/year ends.
 * This does not align the result to the first day of a calendar period.
 * @throws {RangeError} If the date is invalid or arithmetic overflows.
 * @throws {Error} If the cadence is unsupported.
 */
export const getPeriodStartDate = (
  endDate: string,
  cadence: TimeCadence,
): string => {
  const date = toPlainDate(endDate);
  if (!date) throw new RangeError(`Invalid date: ${endDate}`);
  const dateOnly = date.toString();
  switch (cadence) {
    case "Day":
      return dateOnly;
    case "Week":
      return date.subtract({ days: 7 }).toString();
    case "Month":
      return date.subtract({ months: 1 }).toString();
    case "Quarter":
      return date.subtract({ months: 3 }).toString();
    case "Year":
      return date.subtract({ years: 1 }).toString();
    default:
      throw new Error(`Unsupported time cadence: ${cadence}`);
  }
};

/**
 * Formats a Date in UTC using an English date and 12-hour time to the minute,
 * for example "Jan 1, 2026, 12:30 AM". The timezone is not included in the label.
 * @returns "Invalid Date" if the input Date is invalid.
 */
export const formatDateTime = (date: Date): string => {
  return date.toLocaleDateString("en-US", {
    ...DATE_FORMAT_OPTIONS,
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "UTC",
  });
};

/**
 * Formats a Date relative to the current UTC calendar date, ignoring time of day.
 * Uses Today, Yesterday, Tomorrow, "N units ago", or "in N units".
 * Longer spans use approximate 7-day weeks, 30-day months, and 365-day years,
 * with whole-unit counts rounded down rather than calendar arithmetic.
 * @throws {RangeError} If the input Date is invalid.
 */
export const formatRelativeTime = (date: Date): string => {
  const plainDate = toPlainDate(date);
  if (!plainDate) throw new RangeError("Invalid date");
  const diffInDays = daysBetween(plainDate.toString(), todayUTC().toString());

  if (diffInDays === 0) return "Today";
  if (diffInDays === 1) return "Yesterday";
  if (diffInDays === -1) return "Tomorrow";

  const days = Math.abs(diffInDays);
  const [count, unit] =
    days < 7
      ? [days, "day"]
      : days < 30
        ? [Math.floor(days / 7), "week"]
        : days < 365
          ? [Math.floor(days / 30), "month"]
          : [Math.floor(days / 365), "year"];
  const label = `${count} ${unit}${count === 1 ? "" : "s"}`;
  return diffInDays < 0 ? `in ${label}` : `${label} ago`;
};

export default {
  toISODate,
  parsePlainDate,
  toPlainDate,
  todayUTC,
  fromLocalCalendarDate,
  toLocalCalendarDate,
  formatDate,
  formatSQLDateString,
  isDateRangeLongerThanOneYear,
  parseUTCDateOnly,
  daysBetween,
  getPeriodStartDate,
  formatDateTime,
  formatRelativeTime,
};
